import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilmIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ExportProgress } from '../../utils/ffmpegExporter';

interface ExportLoadingOverlayProps {
  progress: ExportProgress;
  onCancel?: () => void;
}

export default function ExportLoadingOverlay({ progress, onCancel }: ExportLoadingOverlayProps) {
  const { t } = useTranslation(['editor', 'common']);
  const getStageText = (stage: ExportProgress['stage']) => {
    switch (stage) {
      case 'loading':
        return t('editor:exportOverlay.initEnv');
      case 'downloading':
        return t('editor:exportOverlay.downloadResources');
      case 'processing':
        return t('editor:exportOverlay.processClips');
      case 'finalizing':
        return t('editor:exportOverlay.generateFinal');
      case 'complete':
        return t('editor:exportOverlay.complete');
      default:
        return t('editor:exportOverlay.processing');
    }
  };

  const isComplete = progress.stage === 'complete';

  return (
    <div className="fixed inset-0 z-[100] bg-gray-900/95 backdrop-blur-sm flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        {/* 主卡片 */}
        <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700">
          {/* 图标 */}
          <div className="flex justify-center mb-6">
            {isComplete ? (
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-12 h-12 text-green-400" />
              </div>
            ) : (
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center relative">
                <FilmIcon className="w-12 h-12 text-blue-400" />
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-400 animate-spin" />
              </div>
            )}
          </div>

          {/* 标题 */}
          <h3 className="text-2xl font-bold text-center text-white mb-2">
            {isComplete ? t('editor:exportOverlay.exportSuccess') : t('editor:exportOverlay.exportingVideo')}
          </h3>

          {/* 阶段描述 */}
          <p className="text-center text-gray-400 mb-6">
            {getStageText(progress.stage)}
          </p>

          {/* 进度条 */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>{progress.message}</span>
              <span>{Math.round(progress.progress)}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${isComplete ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                style={{ width: `${progress.progress}%` }}
              />
            </div>
          </div>

          {/* 片段进度 */}
          {progress.currentClip !== undefined && progress.totalClips !== undefined && (
            <div className="mb-6 p-4 bg-gray-700/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-300">{t('editor:exportOverlay.currentClip')}</span>
                <span className="text-white font-medium">
                  {progress.currentClip} / {progress.totalClips}
                </span>
              </div>
            </div>
          )}

          {/* 提示信息 */}
          {!isComplete && (
            <div className="space-y-2 mb-6">
              <div className="flex items-start gap-2 text-sm text-gray-400">
                <span className="text-yellow-400 mt-0.5">•</span>
                <span>{t('editor:exportOverlay.exportMayTakeMinutes')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-400">
                <span className="text-yellow-400 mt-0.5">•</span>
                <span>{t('editor:exportOverlay.doNotCloseOrRefresh')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-400">
                <span className="text-yellow-400 mt-0.5">•</span>
                <span>{t('editor:exportOverlay.higherQualityLongerTime')}</span>
              </div>
            </div>
          )}

          {/* 操作按钮 */}
          {!isComplete && onCancel && (
            <button
              onClick={onCancel}
              className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
            >
              {t('editor:exportOverlay.cancelExport')}
            </button>
          )}

          {isComplete && (
            <div className="text-center text-sm text-gray-400">
              {t('editor:exportOverlay.autoDownload')}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        {!isComplete && (
          <div className="mt-4 text-center text-sm text-gray-500">
            <p>{t('editor:export.progress')}: {getStageText(progress.stage)}</p>
          </div>
        )}
      </div>
    </div>
  );
}
